<script setup lang="ts">
import RlErrorAlert from '@/components/common/RlErrorAlert.vue'
import StatusBadge from '@/components/common/StatusBadge.vue'
import RcCustomerSelect from '@/components/rc/common/RcCustomerSelect.vue'
import RcWorkOrderSelect from '@/components/rc/common/RcWorkOrderSelect.vue'
import { useContainers } from '@/composables/rc/containers'
import { useInvoices } from '@/composables/rc/invoices'
import { ContainerStatus, type IContainerDashboard } from '@/types/rc/containers'
import type { TableColumnCtx } from 'element-plus'
import type { IContainerDashboardFilters } from '@/types/rc/filters'
import { ShipmentType } from '@/types/rc/shipments'
import { DEFAULT_PAGE_SIZES, DEFAULT_PAGINATION_LAYOUT } from '@/types/shared/pagination'
import { useDebounceFn } from '@vueuse/core'
import { computed, reactive, ref } from 'vue'
import { useRouter } from 'vue-router'

const router = useRouter()

const { getContainersQuery } = useContainers()

// BE filters
const filters = reactive<IContainerDashboardFilters>({
  page: 1,
  per_page: DEFAULT_PAGE_SIZES[0],
  kind: ShipmentType.Outbound,
  status: ContainerStatus.ReadyForBilling,
  customer_id: undefined,
  invoiced: false,
  number: undefined,
})

// Buffers for filters:
const selectedWorkOrder = ref()
const selectedCustomer = ref()
const selectedStatus = ref(ContainerStatus.ReadyForBilling)

const lookupByNumberFragment = ref('')
const searchFragment = useDebounceFn(
  () => {
    filters.number = lookupByNumberFragment.value.length ? lookupByNumberFragment.value : undefined
  },
  500,
  { maxWait: 3000 }
)

const containerStatuses = Object.values(ContainerStatus)

// BE data
const { data, isPending: isQueryPending, error, isError } = getContainersQuery(filters)

// Checkboxes selection
const selection = ref<IContainerDashboard[]>()
const onSelection = (containers: IContainerDashboard[]) => {
  selection.value = containers
}

// Event handlers

const onRowClick = (row: IContainerDashboard, col: TableColumnCtx<IContainerDashboard> | null) => {
  if (col?.type != 'selection') router.push({ name: 'rc-logistics-container-details', params: { containerID: row.id } })
}

const { createInvoiceMutation } = useInvoices()
const { mutate, isPending: isCreationPending } = createInvoiceMutation

const isCreatingInvoice = ref(false)
const generateInvoice = () => {
  if (!canGenerateInvoice.value) return

  isCreatingInvoice.value = true

  const wo = selection.value![0].work_order_number

  mutate(
    {
      work_order_number: wo,
      container_ids: selection.value!.map((container) => container.id),
    },
    {
      onSuccess: () => {
        selection.value = []
      },
      onSettled: () => {
        isCreatingInvoice.value = false
      },
    }
  )
}

// UI state - Generate invoice button enabling and feedback

const anyContainersSelected = computed(() => selection.value && selection.value?.length > 0)

const allSelectedBelongToSameWO = computed(
  () => new Set(selection.value?.map((cont) => cont.work_order_number)).size == 1
)
const allSelectedAreReady = computed(() =>
  selection.value?.map((cont) => cont.status).every((s) => s === ContainerStatus.ReadyForBilling)
)

const generateButtonFeedbackMessage = computed(() =>
  !allSelectedBelongToSameWO.value
    ? 'Selected containers must belong to the same WO in order to generate an invoice'
    : !allSelectedAreReady.value
      ? 'Selected containers must be Ready for billing in order to generate an invoice'
      : 'Select containers to generate an invoice'
)

const canGenerateInvoice = computed(
  () => anyContainersSelected.value && allSelectedAreReady.value && allSelectedBelongToSameWO.value
)

// UI state - Selectable checkboxes in containers table
const selectable = (row: IContainerDashboard) =>
  row.assigned_miners > 0 && row.status == ContainerStatus.ReadyForBilling
</script>

<template>
  <RlErrorAlert v-if="isError && !isQueryPending" :error="error" />
  <template v-else>
    <div class="flex gap-2 items-center">
      <el-input
        v-model="lookupByNumberFragment"
        clearable
        class="w-48"
        :loading="isQueryPending"
        placeholder="Container #"
        @change="searchFragment"
      />
      <RcWorkOrderSelect
        v-model="selectedWorkOrder"
        class="w-64"
        @change="(wo: { number?: string } | undefined) => (filters.work_order_number = wo?.number)"
      />
      <RcCustomerSelect
        v-model="selectedCustomer"
        class="w-48"
        @change="
          (customer) => {
            filters.customer_id = customer?.id
          }
        "
      />
      <el-select
        v-model="selectedStatus"
        class="w-48"
        placeholder="Status"
        clearable
        @change="(st) => (filters.status = st)"
      >
        <el-option v-for="status of containerStatuses" :key="status" :label="status" :value="status" />
      </el-select>

      <!-- Generate invoice button -->
      <el-tooltip :content="generateButtonFeedbackMessage" :disabled="canGenerateInvoice || !anyContainersSelected">
        <el-button
          class="ml-auto"
          size="large"
          type="primary"
          :disabled="!canGenerateInvoice || isCreationPending"
          :loading="isCreationPending"
          @click="generateInvoice"
          >Generate invoice</el-button
        >
      </el-tooltip>
    </div>
    <el-table
      :data="data?.data"
      :loading="isQueryPending"
      row-class-name="cursor-pointer"
      @selection-change="onSelection"
      @row-click="onRowClick"
    >
      <template #empty> No data </template>
      <el-table-column type="selection" :selectable="selectable" @click.stop.prevent />
      <el-table-column label="Customer" prop="customer.name" />
      <el-table-column label="Work order #" prop="work_order_number" />
      <el-table-column label="Container #" prop="number" />
      <el-table-column :width="110" label="Miners" prop="assigned_miners" />
      <el-table-column :width="180" label="Status">
        <template #default="{ row }">
          <StatusBadge :status="row.status" />
        </template>
      </el-table-column>
    </el-table>
    <el-pagination
      v-if="data && data.total_pages > 0"
      v-model:current-page="filters.page"
      v-model:page-size="filters.per_page"
      class="self-end"
      :page-sizes="DEFAULT_PAGE_SIZES"
      :page-count="data.total_pages"
      :total="data.total_records"
      :disabled="isQueryPending"
      :layout="DEFAULT_PAGINATION_LAYOUT"
      background
    />
  </template>
</template>
